import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/lib/auth";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import DashboardLayout from "@/pages/DashboardLayout";
import Dashboard from "@/pages/Dashboard";
import Markets from "@/pages/Markets";
import Portfolio from "@/pages/Portfolio";
import Trading from "@/pages/Trading";
import AICopilot from "@/pages/AICopilot";
import Billing from "@/pages/Billing";
import Admin from "@/pages/Admin";
import AdminProviders from "@/pages/AdminProviders";

function Protected({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neo-bg text-neutral-400 font-mono text-xs uppercase tracking-widest">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !user.is_admin) return <Navigate to="/app/dashboard" replace />;
  return children;
}

function RoutedApp() {
  const location = useLocation();
  // Detect the Google OAuth return (session_id in the URL fragment) synchronously,
  // before Protected/AuthProvider can bounce to /login.
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route
        path="/app"
        element={
          <Protected>
            <DashboardLayout />
          </Protected>
        }
      >
        <Route index element={<Navigate to="/app/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="markets" element={<Markets />} />
        <Route path="portfolio" element={<Portfolio />} />
        <Route path="trading" element={<Trading />} />
        <Route path="ai" element={<AICopilot />} />
        <Route path="billing" element={<Billing />} />
        <Route
          path="admin"
          element={
            <Protected adminOnly>
              <Admin />
            </Protected>
          }
        />
        <Route
          path="admin/providers"
          element={
            <Protected adminOnly>
              <AdminProviders />
            </Protected>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster
            theme="dark"
            position="top-right"
            toastOptions={{
              style: {
                background: "#141414",
                border: "1px solid #262626",
                color: "#fff",
                borderRadius: 0,
                fontFamily: '"Outfit", sans-serif',
              },
            }}
          />
          <RoutedApp />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
